import React, { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { BaseLayoutContext, BaseLayoutSection } from '../../types/base-layout'
import { PartnerRegionAssociation } from '@/features/partners/types/partner.type'
import { sanitizeHtmlContent } from '../../utils/sanitize'

interface PartnerGiveListSectionProps {
  section: BaseLayoutSection
  context: BaseLayoutContext
}

export function PartnerGiveListSection({ section, context }: PartnerGiveListSectionProps) {
  const partners = useMemo(() => {
    const allPartners = context.partners ?? []
    
    // Filter to only show partners with give links (regardless of active status)
    return allPartners.filter((association) => {
      const org = association.organization
      const hasOrganization = !!org
      const giveLink = org?.giveLink
      const hasGiveLink = !!giveLink && giveLink.trim().length > 0
      
      return hasOrganization && hasGiveLink
    })
  }, [context.partners])

  return (
    <Card key={section.id}>
      <CardHeader>
        <CardTitle>{section.title ?? 'Partner Give List'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {(() => {
          const safeHtml = sanitizeHtmlContent(section.content)
          return safeHtml ? (
            <div className="prose max-w-none text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: safeHtml }} />
          ) : null
        })()}
        {partners.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No partners with giving links available at this time.
          </p>
        ) : (
          <div className="space-y-3">
            {partners.map((association) => {
              const partner = association.organization
              return (
                <div
                  key={association.id}
                  className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-4"
                >
                  <h4 className="font-semibold text-foreground">{partner.name}</h4>
                  {partner.giveLink && (
                    <div>
                      <a
                        href={partner.giveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm"
                      >
                        {partner.name} Giving Link
                      </a>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

